<template>
  <div class="teams-section">
    <div class="container padding-0">
      <div class="text-center mb-5">
        <h2 class="teams-title">Our Teams</h2>
        <p class="teams-description">
          At Cowlar Design Studio, our multi-disciplinary engineering teams
          unite, bringing diverse expertise to create innovative solutions.
          Together, we transform ideas into reality, pushing the boundaries of
          design and technology.
        </p>
      </div>

      <Carousel class="teams-carousel" :wrap-around="true">
        <Slide v-for="slide in info" :key="slide" class="carousel-slide">
          <div class="team-card">
            <div class="team-content">
              <h3 class="team-name">{{ slide.title }}</h3>
              <p class="team-description">{{ slide.description }}</p>
            </div>
            <div class="team-image-container">
              <img :src="slide.picture" alt="team image" class="team-image" />
            </div>
          </div>
        </Slide>
        <template #addons>
          <Navigation />
          <Pagination />
        </template>
      </Carousel>
    </div>
  </div>
</template>

<script>
import { Carousel, Navigation, Pagination, Slide } from "vue3-carousel";
import { useRouter } from "vue-router";
import "vue3-carousel/dist/carousel.css";
import { projectInfo } from "../data/slider";

export default {
  name: "RecentWork",
  components: {
    Carousel,
    Slide,
    Pagination,
    Navigation,
  },

  setup() {
    const router = useRouter();
    const info = projectInfo;
    return { info, router };
  },
};
</script>

<style scoped>
.teams-section {
  padding: 80px 0;
  background-color: #1a1a1a;
}

.teams-title {
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #f47726 0%, #ff9a56 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 2rem;
  letter-spacing: -0.01em;
}

.teams-description {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #b5b8ba;
  max-width: 800px;
  margin: 0 auto 3rem;
  font-weight: 300;
}

.teams-carousel {
  margin-top: 2rem;
}

.carousel-slide {
  padding: 0 15px;
}

.team-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2.5rem;
  display: flex;
  align-items: center;
  gap: 3rem;
  transition: all 0.3s ease;
  min-height: 350px;
}

.team-card:hover {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(244, 119, 38, 0.2);
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.team-content {
  flex: 1;
}

.team-name {
  font-size: 1.75rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 1rem;
  line-height: 1.2;
}

.team-description {
  font-size: 1rem;
  line-height: 1.7;
  color: #d0d0d0;
  text-align: justify;
  font-weight: 300;
}

.team-image-container {
  flex: 0 0 320px;
}

.team-image {
  width: 100%;
  height: 240px;
  object-fit: contain;
  border-radius: 12px;
}

/* Carousel Controls */
::v-deep(.carousel__pagination) {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  gap: 6px;
}

::v-deep(.carousel__pagination-button::after) {
  display: none;
}

::v-deep(.carousel__pagination-button) {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
  border: none;
  transition: all 0.2s ease;
}

::v-deep(.carousel__pagination-button:hover) {
  background-color: rgba(244, 119, 38, 0.7);
  transform: scale(1.2);
}

::v-deep(.carousel__pagination-button--active) {
  background-color: #f47726;
  transform: scale(1.1);
}

::v-deep(.carousel__prev),
::v-deep(.carousel__next) {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  width: 44px;
  height: 44px;
  color: #ffffff;
  transition: all 0.2s ease;
}

::v-deep(.carousel__prev:before),
::v-deep(.carousel__next:before) {
  content: none;
}

::v-deep(.carousel__prev:hover),
::v-deep(.carousel__next:hover) {
  background-color: #f47726;
  border-color: #f47726;
}

/* Mobile Responsiveness */
@media (max-width: 1024px) {
  .team-card {
    flex-direction: column;
    text-align: center;
    gap: 2rem;
    padding: 2rem;
  }

  .team-image-container {
    flex: none;
  }

  .team-image {
    width: 280px;
    height: 200px;
  }
}

@media (max-width: 768px) {
  .teams-section {
    padding: 60px 0;
  }

  .teams-title {
    font-size: 2rem;
  }

  .teams-description {
    font-size: 1rem;
    padding: 0 1rem;
  }

  .team-card {
    padding: 1.5rem;
    margin: 0 0.5rem;
  }

  .team-name {
    font-size: 1.5rem;
  }

  .team-description {
    font-size: 0.95rem;
  }

  .team-image {
    width: 240px;
    height: 180px;
  }

  ::v-deep(.carousel__prev),
  ::v-deep(.carousel__next) {
    width: 36px;
    height: 36px;
  }
}

@media (max-width: 576px) {
  .teams-section {
    padding: 50px 0;
  }

  .teams-title {
    font-size: 1.75rem;
  }

  .team-card {
    padding: 1.25rem;
  }

  .team-name {
    font-size: 1.3rem;
  }

  .team-description {
    font-size: 0.9rem;
  }

  .team-image {
    width: 200px;
    height: 150px;
  }
}

/* Legacy compatibility */
.container {
  text-align: center;
}

.padding-0 {
  padding: 0 !important;
}

::v-deep(.row) {
  margin-top: 0.7vh;
}
</style>
